import { KdsLink, KdsText, VStack } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import { HighlightIA } from "@/components/OpenBrief/HighlightIA/HighlightIA";
import { FieldValueSource } from "@/hooks";
import { type TechnicalSpecificationsCategoryListProps } from "./TechnicalSpecificationsCategoryList.types";
import { computeVisibleCategories } from "./TechnicalSpecificationsCategoryList.utils";

export const TechnicalSpecificationsCategoryList = ({
  optionsByCategory,
}: TechnicalSpecificationsCategoryListProps) => {
  const { t } = useTranslate();
  const [showAll, setShowAll] = useState(false);

  const totalOptions = optionsByCategory.reduce(
    (sum, [, options]) => sum + options.length,
    0
  );
  const hasMoreOrLess = totalOptions > 3 || optionsByCategory.length >= 2;

  const getCategoryName = (category: string) => {
    switch (category) {
      case "1":
        return t("expertMode.technicalSpecifications.category1");
      case "2":
        return t("expertMode.technicalSpecifications.category2");
      default:
        return category;
    }
  };

  const visibleCategories = computeVisibleCategories(
    optionsByCategory,
    showAll
  );

  return (
    <VStack gap="sp-lg">
      {visibleCategories.map(([category, options]) => (
        <VStack key={category} gap="sp-sm">
          <KdsText size="subtitle-sm">{getCategoryName(category)}</KdsText>

          <div className="grid grid-cols-3 gap-sp-xl p-sp-md">
            {options.map((option) => {
              const isFromAssistant =
                option.source === FieldValueSource.ASSISTANT;
              return (
                <VStack key={option.id} gap="sp-xs3">
                  <KdsText size="caption-sm" color="text-bodyvariant">
                    {t(option.name)}
                  </KdsText>
                  {isFromAssistant ? (
                    <HighlightIA>{option.value}</HighlightIA>
                  ) : (
                    <KdsText size="subtitle-sm">{option.value}</KdsText>
                  )}
                </VStack>
              );
            })}
          </div>
        </VStack>
      ))}

      {hasMoreOrLess && (
        <KdsLink
          href="#"
          state="neutral"
          size="sm"
          onPress={() => setShowAll((prev) => !prev)}
        >
          {showAll
            ? t("expertMode.technicalSpecifications.showLess")
            : t("expertMode.technicalSpecifications.showMore")}
        </KdsLink>
      )}
    </VStack>
  );
};
